import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle } from "@my-saas/ui";
import { useState } from "react";
import { Helmet } from "react-helmet-async";

import { useRawAlerts } from "@/client/services/platform";

const SOURCE_FILTERS = ["", "oref_alerts", "oref_history", "oref_history2", "oref_mqtt", "tzevaadom", "tzevaadom-system"] as const;

export const PlatformRawAlertsPage = () => {
  const [source, setSource] = useState<string>("");
  const [offset, setOffset] = useState(0);
  const limit = 50;
  const { page, loading, refetch } = useRawAlerts({ limit, offset, source: source || undefined });

  return (
    <>
      <Helmet>
        <title>Platform · Raw OREF alerts</title>
      </Helmet>

      <div className="space-y-6">
        <div className="flex items-baseline justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">Raw OREF alerts</h1>
            <p className="text-sm text-muted-foreground">
              Platform-level alert log (deduped across all sources by alertId). Updates every 10s.
            </p>
          </div>
          {page && (
            <div className="text-xs text-muted-foreground">
              Showing <span className="font-mono">{page.rows.length}</span> of{" "}
              <span className="font-mono">{page.total}</span>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">Source:</span>
          {SOURCE_FILTERS.map((s) => (
            <Button
              key={s || "all"}
              size="sm"
              variant={source === s ? "primary" : "outline"}
              onClick={() => {
                setSource(s);
                setOffset(0);
              }}
            >
              {s || "all"}
            </Button>
          ))}
          <Button size="sm" variant="ghost" onClick={() => void refetch()}>
            ↻ refresh
          </Button>
        </div>

        {loading && !page && <p className="text-sm text-muted-foreground">Loading…</p>}

        {page && page.rows.length === 0 && (
          <Card>
            <CardHeader>
              <CardTitle>No alerts yet</CardTitle>
              <CardDescription>
                When the OREF HTTP poller / MQTT / Tzevaadom emit alerts, they'll appear here.
              </CardDescription>
            </CardHeader>
          </Card>
        )}

        <div className="space-y-2">
          {page?.rows.map((a) => (
            <Card key={a.id} className="overflow-hidden">
              <CardContent className="grid grid-cols-1 gap-2 p-4 sm:grid-cols-[auto,1fr,auto] sm:items-center">
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="font-mono text-xs">{a.source}</Badge>
                  <Badge variant="secondary" className="text-xs">cat {a.category}</Badge>
                </div>
                <div>
                  <div className="font-medium" dir="rtl">{a.title}</div>
                  <div className="text-xs text-muted-foreground" dir="rtl">
                    {a.locations.slice(0, 6).join(", ")}
                    {a.locations.length > 6 ? ` +${a.locations.length - 6} more` : ""}
                  </div>
                </div>
                <div className="text-xs text-muted-foreground sm:text-right">
                  <div>{new Date(a.alertDate).toLocaleString()}</div>
                  <div className="font-mono">#{a.alertId}</div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {page && page.total > limit && (
          <div className="flex items-center justify-center gap-2 pt-2">
            <Button size="sm" variant="outline" onClick={() => setOffset(Math.max(0, offset - limit))} disabled={offset === 0}>
              ← Newer
            </Button>
            <span className="text-xs text-muted-foreground">
              {offset + 1}-{Math.min(offset + limit, page.total)} / {page.total}
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setOffset(offset + limit)}
              disabled={offset + limit >= page.total}
            >
              Older →
            </Button>
          </div>
        )}
      </div>
    </>
  );
};
